/**
 * Puts in-stock products on early collection pages and sold-out products on later pages.
 * Keeps the same total page count as normal pagination (no extra pages).
 * Uses Shopify availability filters under the hood while keeping a single virtual ?page= URL.
 */
window.CollectionSoldOutSort = (function () {
  const AVAILABILITY_PARAM = 'filter.v.availability';
  let cache = new Map();
  let appliedKey = null;
  let inFlight = null;
  let bound = false;

  function getConfig() {
    const node = document.getElementById('CollectionStockSortConfig');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function currentVirtualPage() {
    const page = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  function hasManualAvailabilityFilter(params) {
    return params.getAll(AVAILABILITY_PARAM).length > 0;
  }

  function stripAvailability(params) {
    const next = new URLSearchParams(params);
    next.delete(AVAILABILITY_PARAM);
    return next;
  }

  function pageCount(count, perPage) {
    if (!count || count < 1) return 0;
    return Math.ceil(count / perPage);
  }

  function mapVirtualPage(virtualPage, config) {
    const perPage = config.perPage;
    const availableCount = config.availableCount || 0;
    const soldOutCount = config.soldOutCount || 0;
    const total = availableCount + soldOutCount;
    const totalPages = Math.max(pageCount(total, perPage), 1);
    const page = Math.min(Math.max(virtualPage, 1), totalPages);
    const start = (page - 1) * perPage;
    const end = Math.min(start + perPage, total);

    if (availableCount < 1) {
      return {
        mode: 'soldout',
        start,
        end,
        totalPages,
        page,
        key: `0:${start}-${end}`,
      };
    }

    if (end <= availableCount) {
      return {
        mode: 'available',
        fetchPage: page,
        totalPages,
        page,
        key: `1:${page}`,
      };
    }

    if (start >= availableCount) {
      return {
        mode: 'soldout',
        start,
        end,
        totalPages,
        page,
        key: `0:${start}-${end}`,
      };
    }

    return {
      mode: 'mixed',
      availableNeeded: availableCount - start,
      soldOutNeeded: end - availableCount,
      availableFetchPage: Math.floor(start / perPage) + 1,
      totalPages,
      page,
      key: `mixed:${page}`,
    };
  }

  function virtualPageUrl(page) {
    const params = stripAvailability(new URLSearchParams(window.location.search));
    if (page <= 1) params.delete('page');
    else params.set('page', String(page));
    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  function setPending(isPending) {
    const collection = document.querySelector('#ProductGridContainer .collection');
    if (!collection) return;
    collection.classList.toggle('collection--stock-sort-pending', isPending);
    collection.classList.toggle('loading', isPending);
  }

  async function fetchSectionHtml(config, availability, fetchPage) {
    const params = stripAvailability(new URLSearchParams(window.location.search));
    params.delete('page');
    params.set(AVAILABILITY_PARAM, availability);
    if (fetchPage > 1) params.set('page', String(fetchPage));
    params.set('section_id', config.sectionId);

    const url = `${window.location.pathname}?${params.toString()}`;
    if (cache.has(url)) return cache.get(url);

    const response = await fetch(url);
    const html = await response.text();
    cache.set(url, html);
    return html;
  }

  async function fetchGridItems(config, availability, fetchPage) {
    const html = await fetchSectionHtml(config, availability, fetchPage);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return [...doc.querySelectorAll('#product-grid > .grid__item')];
  }

  async function resolvePageItems(config, mapping) {
    const perPage = config.perPage;

    if (mapping.mode === 'available') {
      return fetchGridItems(config, '1', mapping.fetchPage);
    }

    if (mapping.mode === 'mixed') {
      const availableItems = await fetchGridItems(config, '1', mapping.availableFetchPage);
      const soldOutItems = await fetchGridItems(config, '0', 1);
      const availableSlice = availableItems.slice(-mapping.availableNeeded);
      const soldOutSlice = soldOutItems.slice(0, mapping.soldOutNeeded);
      return availableSlice.concat(soldOutSlice);
    }

    // soldout — may span one or two Shopify pages after availability filter
    const soldStart = mapping.start - (config.availableCount || 0);
    const soldEnd = mapping.end - (config.availableCount || 0);
    const firstFetchPage = Math.floor(soldStart / perPage) + 1;
    const lastFetchPage = Math.floor((soldEnd - 1) / perPage) + 1;
    let items = [];

    for (let p = firstFetchPage; p <= lastFetchPage; p++) {
      items = items.concat(await fetchGridItems(config, '0', p));
    }

    const offsetInFirst = soldStart % perPage;
    return items.slice(offsetInFirst, offsetInFirst + (soldEnd - soldStart));
  }

  function replaceProductGrid(items) {
    const liveGrid = document.getElementById('product-grid');
    if (!liveGrid) return false;

    liveGrid.replaceChildren(...items);
    liveGrid.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.add('scroll-trigger--cancel');
    });
    return true;
  }

  function buildPaginationParts(current, total) {
    if (total <= 1) return [];
    const parts = [];
    const pushPage = (n) => parts.push({ type: 'page', title: String(n), page: n, current: n === current });
    const pushEllipsis = () => parts.push({ type: 'ellipsis', title: '…' });

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pushPage(i);
      return parts;
    }

    pushPage(1);
    if (current > 3) pushEllipsis();

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pushPage(i);

    if (current < total - 2) pushEllipsis();
    pushPage(total);
    return parts;
  }

  function caretSvg() {
    return '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-caret" fill="none" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 1 1 .708-.708L5 4.293 8.646.646a.5.5 0 0 1 .708 0Z" fill="currentColor"/></svg>';
  }

  function renderPagination(current, total) {
    const collection = document.querySelector('#ProductGridContainer .collection');
    if (!collection) return;

    let wrapper = collection.querySelector('.pagination-wrapper');
    if (total <= 1) {
      if (wrapper) wrapper.remove();
      return;
    }

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'pagination-wrapper';
      const description = collection.querySelector('.collection__description--bottom');
      if (description) collection.insertBefore(wrapper, description);
      else collection.appendChild(wrapper);
    }

    wrapper.setAttribute('data-page', String(current));
    const parts = buildPaginationParts(current, total);
    const prevUrl = current > 1 ? virtualPageUrl(current - 1) : null;
    const nextUrl = current < total ? virtualPageUrl(current + 1) : null;
    const caret = caretSvg();

    wrapper.innerHTML = `
      <nav class="pagination" role="navigation" aria-label="Pagination">
        <ul class="pagination__list list-unstyled" role="list">
          ${
            prevUrl
              ? `<li>
                  <a href="${prevUrl}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Previous" data-stock-sort-page="${current - 1}">
                    <span class="svg-wrapper">${caret}</span>
                  </a>
                </li>`
              : ''
          }
          ${parts
            .map((part) => {
              if (part.type === 'ellipsis') {
                return `<li><span class="pagination__item">${part.title}</span></li>`;
              }
              if (part.current) {
                return `<li>
                  <a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${part.title}">
                    ${part.title}
                  </a>
                </li>`;
              }
              return `<li>
                <a href="${virtualPageUrl(part.page)}" class="pagination__item link" aria-label="Page ${part.title}" data-stock-sort-page="${part.page}">
                  ${part.title}
                </a>
              </li>`;
            })
            .join('')}
          ${
            nextUrl
              ? `<li>
                  <a href="${nextUrl}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Next" data-stock-sort-page="${current + 1}">
                    <span class="svg-wrapper">${caret}</span>
                  </a>
                </li>`
              : ''
          }
        </ul>
      </nav>
    `;
  }

  async function goToVirtualPage(page, { pushState = true } = {}) {
    const url = virtualPageUrl(page);
    if (pushState) history.pushState({ stockSortPage: page }, '', url);
    else history.replaceState({ stockSortPage: page }, '', url);
    appliedKey = null;
    await apply();
  }

  function bindEvents() {
    if (bound) return;
    bound = true;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-stock-sort-page]');
      if (!link) return;
      const page = parseInt(link.getAttribute('data-stock-sort-page'), 10);
      if (!Number.isFinite(page)) return;
      event.preventDefault();
      goToVirtualPage(page);
    });

    window.addEventListener('popstate', () => {
      appliedKey = null;
      apply();
    });
  }

  async function apply() {
    const config = getConfig();
    if (!config?.enabled || !config.sectionId) return;

    const params = new URLSearchParams(window.location.search);
    if (config.availabilityFilterActive || hasManualAvailabilityFilter(params)) {
      appliedKey = null;
      setPending(false);
      return;
    }

    if (!config.soldOutCount || config.soldOutCount < 1) {
      appliedKey = null;
      setPending(false);
      return;
    }

    const virtualPage = currentVirtualPage();
    const mapping = mapVirtualPage(virtualPage, config);
    const clampedPage = mapping.page;
    if (clampedPage !== virtualPage) {
      history.replaceState({}, '', virtualPageUrl(clampedPage));
    }

    if (appliedKey === mapping.key) {
      setPending(false);
      return;
    }

    const requestKey = mapping.key;
    setPending(true);

    const run = (async () => {
      try {
        const items = await resolvePageItems(config, mapping);
        const latest = mapVirtualPage(currentVirtualPage(), getConfig() || config);
        if (requestKey !== latest.key) return;
        replaceProductGrid(items);
        renderPagination(clampedPage, mapping.totalPages);
        appliedKey = mapping.key;
      } catch (e) {
        console.error('CollectionSoldOutSort failed', e);
      } finally {
        setPending(false);
      }
    })();

    inFlight = run;
    await run;
    if (inFlight === run) inFlight = null;
  }

  function invalidateCache() {
    cache = new Map();
    appliedKey = null;
  }

  bindEvents();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      apply();
    });
  } else {
    apply();
  }

  return { apply, invalidateCache };
})();
